import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Modal, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { money, paymentReads, plans as plansApi } from '../../api/fee'
import type { Plan } from '../../api/types'
import { ConfirmButton, Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { failedTransactionMessage } from '../../lib/checkout'
import { formatDateTime, formatPrice } from '../../lib/format'
import { hasMethod } from '../../lib/paymentHelpers'
import { useEmailParam } from '../../lib/useEmailParam'
import { useMoneyAction } from '../../lib/useMoneyAction'
import { AdminUserLocalMenu } from './AdminUserLocalMenu'

function statusText(plan: Plan): string {
  const s = plan.subscription
  const at = formatDateTime(s?.expiresAt)
  switch (Number(s?.status ?? 0)) {
    case 0:
      return 'Not started'
    case 1:
      return `Subscribed, will be automatically renewed at: ${at}`
    case 3:
      return `Overdue, expires at: ${at}`
    case 6:
      return `Canceled, expires at: ${at}`
    default:
      return ''
  }
}

const EDITABLE_STATUSES: [number, string][] = [
  [1, 'Active'],
  [3, 'Overdue'],
  [6, 'Canceled'],
]

/**
 * Port of `AdminUserSubscriptionListComponent` (`…/admin/users/subscriptions?email=`).
 * BUSINESS-CRITICAL, admin acting on an end-user:
 * - "Start subscription and make user pay setup fee" → `POST
 *   …/admin/payment/startonline?email=` `{cause: manual, type: plan, plans}`
 *   — charges the user's saved card (per-submit idempotency key);
 * - "Start subscription without setup fee payment" → `POST
 *   …/admin/payment/subscription?email=&plan=`;
 * - "Edit" → `PATCH …/admin/payment/subscription?email=&plan=`
 *   `{status, expiresAtDate: "YYYY-MM-DD"}` (field name kept verbatim).
 * The legacy confirm() texts are kept as inline confirmations.
 */
export function AdminUserSubscriptionListPage() {
  const { projectId, project } = useProject()
  const email = useEmailParam()
  const q = useApiQuery(() => (email ? plansApi.plansOfUser(projectId, email) : Promise.resolve([])), [projectId, email])
  const pm = useApiQuery(() => (email ? paymentReads.methodOfUser(projectId, email) : Promise.resolve(null)), [projectId, email])
  const [editing, setEditing] = useState<Plan | null>(null)
  const actions = useMoneyAction()
  const withMethod = hasMethod(pm.data)
  const base = `/projects/${encodeURIComponent(projectId)}/admin/plans`

  const startPaid = async (plan: Plan) => {
    const body = { cause: 'manual', type: 'plan' as const, plans: plan.id }
    const tx = await actions.run(`paid-${plan.id}`, `${email}|${plan.id}`, (key) =>
      money.startOnlineForUser(projectId, email, body, key),
    )
    if (tx === undefined) return
    const failure = failedTransactionMessage(tx)
    actions.setFeedback(
      `paid-${plan.id}`,
      failure ? { tone: 'error', text: failure } : tx ? { tone: 'success', text: 'Subscription started and paid.' } : { tone: 'error', text: 'The payment could not be started.' },
    )
    q.refetch()
  }
  const startFree = (plan: Plan) =>
    actions.run(`free-${plan.id}`, `${email}|${plan.id}`, () => money.startSubscriptionForUser(projectId, email, plan.id), () => {
      actions.setFeedback(`free-${plan.id}`, { tone: 'success', text: 'Subscription started without payment.' })
      q.refetch()
    })

  const columns: Column<Plan>[] = [
    { key: 'name', label: 'Plan', format: (_v, p) => <Link to={`${base}/${encodeURIComponent(p.id)}`}>{p.name}</Link> },
    { key: 'price', label: 'Price', align: 'right', numeric: true, format: (_v, p) => formatPrice(p.price, p.currency) },
    { key: 'status', label: 'Status', sortValue: (p) => Number(p.subscription?.status ?? 0), format: (_v, p) => statusText(p) },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, p) => {
        const status = Number(p.subscription?.status ?? 0)
        const price = formatPrice(p.price, p.currency)
        return (
          <span className="portal-inline-actions">
            {status === 0 ? (
              <>
                {withMethod && (
                  <ConfirmButton
                    label={`Start subscription and make user pay setup fee (${price})`}
                    pendingText={`Are you sure you want to start "${p.name}" for ${email} and make them pay ${price}?`}
                    busy={actions.isBusy(`paid-${p.id}`)}
                    disabled={actions.isBusy(`free-${p.id}`)}
                    onConfirm={() => void startPaid(p)}
                  />
                )}
                <ConfirmButton
                  label="Start subscription without setup fee payment"
                  pendingText={`Are you sure you want to start "${p.name}" for ${email} without setup payment?`}
                  busy={actions.isBusy(`free-${p.id}`)}
                  disabled={actions.isBusy(`paid-${p.id}`)}
                  onConfirm={() => void startFree(p)}
                />
              </>
            ) : (
              <button type="button" onClick={() => setEditing(p)}>
                Edit
              </button>
            )}
            <Notice feedback={actions.feedbackFor(`paid-${p.id}`) ?? actions.feedbackFor(`free-${p.id}`)} />
          </span>
        )
      },
    },
  ]

  return (
    <div className="portal-card">
      <PageTitle title={`Subscriptions — ${project.name}`}>
        <AdminUserLocalMenu email={email} title="Subscriptions" />
      </PageTitle>
      {!email && <p className="portal-text-muted">Pick a user from the menu above.</p>}
      {email && pm.data && !withMethod && (
        <p className="portal-notice portal-notice--warning">The user don&apos;t have a payment method yet.</p>
      )}
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={q.data ?? []} rowKey={(p) => p.id} />
        </div>
      </QueryState>
      {editing && (
        <EditSubscription
          projectId={projectId}
          email={email}
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            q.refetch()
          }}
        />
      )}
    </div>
  )
}

function EditSubscription({
  projectId,
  email,
  plan,
  onClose,
  onSaved,
}: {
  projectId: string
  email: string
  plan: Plan
  onClose: () => void
  onSaved: () => void
}) {
  const current = Number(plan.subscription?.status ?? 0)
  const [status, setStatus] = useState(current)
  const [date, setDate] = useState(String(plan.subscription?.expiresAt ?? '').slice(0, 10))
  const actions = useMoneyAction()
  const options = EDITABLE_STATUSES.some(([s]) => s === current) ? EDITABLE_STATUSES : [...EDITABLE_STATUSES, [current, `Current (${current})`] as [number, string]]

  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return actions.setFeedback('save', { tone: 'warning', text: 'Pick an expiry date.' })
    const body = { status, expiresAtDate: date }
    const r = await actions.run('save', JSON.stringify(body), () => money.modifySubscriptionOfUser(projectId, email, plan.id, body))
    if (r) onSaved()
  }

  return (
    <Modal open title={`Edit subscription — ${plan.name}`} onClose={onClose}>
      <form
        className="portal-form"
        aria-label="Edit subscription"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(Number(e.target.value))}>
            {options.map(([s, label]) => (
              <option key={s} value={s} disabled={!EDITABLE_STATUSES.some(([x]) => x === s)}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Expires at
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <div className="portal-inline-actions">
          <button type="submit" disabled={actions.isBusy('save')}>
            {actions.isBusy('save') ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
        <Notice feedback={actions.feedbackFor('save')} />
      </form>
    </Modal>
  )
}
